import { useLanguage } from '@/app/providers';
import { AudioIcon, CloseIcon, IconMic } from '@/assets/images';
import { AppIcon } from '@/components';
import { getFileIcon } from '@/features/chats/utils';
import { Username, UserProfile } from '@/features/users';
import { getLocalUserId, getMessageSenderId } from '@/shared/utils';
import PropTypes from 'prop-types';
import { useEditMessage } from '../../viewmodels/useEditMessage';
import { useMessageFormatter } from '../../viewmodels/useMessageFormatter';
import { useReplyMessage } from '../../viewmodels/useReplyMessage';

/** Renders the media preview thumbnail/icon inside the reply footer strip */
function ReplyMediaPreview({ msgType, media = {} }) {
   if (msgType === 'image' || msgType === 'video') {
      const thumbUrl = media.thumb_image ? `data:image/jpeg;base64,${media.thumb_image}` : null;
      return (
         <div className="reply-media-thumb ct-flexCenter ct-relative">
            {thumbUrl ? (
               <img className="reply-thumb-img" src={thumbUrl} alt={msgType} />
            ) : (
               <span className="reply-thumb-fallback">{msgType === 'video' ? '▶' : '🖼'}</span>
            )}
         </div>
      );
   }

   if (msgType === 'audio') {
      const isRecording = media?.audioType === 'recording';
      return (
         <div className="reply-media-thumb reply-media-thumb--audio ct-flexCenter">
            <AppIcon icon={isRecording ? IconMic : AudioIcon} />
         </div>
      );
   }

   if (msgType === 'file') {
      const fileName = media.fileName || media.file_name || '';
      return (
         <div className="reply-media-thumb reply-media-thumb--doc ct-flexCenter">
            <AppIcon icon={getFileIcon(fileName.split('.').pop())} />
         </div>
      );
   }

   return null;
}

ReplyMediaPreview.propTypes = {
   msgType: PropTypes.string.isRequired,
   media: PropTypes.object,
};

function ReplyMessageFooter({ activeChatJid, isEdit = false }) {
   const { t } = useLanguage();
   const { replyMessage: draftReplyMessage, clearReplyTo } = useReplyMessage({ activeChatJid });
   const { editMessage, clearEditMessage } = useEditMessage({ activeChatJid });

   const message = isEdit ? editMessage : draftReplyMessage;
   const onClear = isEdit ? clearEditMessage : clearReplyTo;

   const { msgType = '', msgBody = {}, deleteStatus } = message || {};
   const { message: textContent = '', media = {}, mentionedUsersIds = [] } = msgBody;

   const hasMedia = ['image', 'video', 'audio', 'file'].includes(msgType);
   let label = media.fileName || media.file_name || 'Document';
   if (msgType === 'audio') label = media?.audioType === 'recording' ? 'Voice message' : 'Audio';
   else if (msgType === 'video') label = 'Video';
   else if (msgType === 'image') label = 'Photo';
   else if (msgType === 'meet') label = 'Meeting';

   let messageContent = textContent || (message ? 'Media' : '');
   if (msgType === 'meet') {
      messageContent = `${label}: ${msgBody?.meet?.title || ''}`;
   } else if (hasMedia) {
      messageContent = (media?.caption || textContent || label).replace(/\+/g, ' ');
   }
   const formatedText = useMessageFormatter(messageContent, { mentionedUsersIds, plainText: true });

   const displayText = deleteStatus ? t('chat.ORIGINAL_MESSAGE_UNAVAILABLE') : formatedText;

   if (!message) return null;

   const senderId = getMessageSenderId({ type: 'chats', message });
   const isSender = senderId === getLocalUserId();

   return (
      <div className="reply-message-footer ct-flex ct-aiCenter">
         <div className="reply-content ct-flex ct-gap12 ct-jcBetween">
            <div className="reply-content-overall ct-flex ct-aiStart ct-gap8 ct-fcol">
               <div className="ct-flex ct-aiCenter ct-gap10">
                  <picture className="reply-avatar ct-icon ct-flexCenter">
                     <UserProfile userId={senderId} className="chat-avatar chat-avatar-fallback ct-flexCenter" />
                  </picture>
                  <Username userId={senderId} isLocal={isSender} />
               </div>
               <div className="reply-text-wrapper ct-flex ct-aiCenter ct-gap8">
                  {hasMedia && <ReplyMediaPreview msgType={msgType} media={media} />}
                  <span className="reply-text ct-ellipsis">{displayText}</span>
               </div>
            </div>
            <div className="reply-right ct-flex ct-aiCenter ct-gap8">
               <button className="close-reply ct-flexCenter ct-cursor ct-noBorder ct-noPadding" onClick={onClear}>
                  <CloseIcon />
               </button>
            </div>
         </div>
      </div>
   );
}

export default ReplyMessageFooter;

ReplyMessageFooter.propTypes = {
   activeChatJid: PropTypes.string.isRequired,
   isEdit: PropTypes.bool,
};
